import React, { Component } from 'react';

import { RouteComponentProps } from '@reach/router';

import { Consumer } from '../../store';
import { IErrorsActions } from '../../store/errors/actions';
import { IProfileActions } from '../../store/user/actions';
import { ISource, IStripeToken, IUserStore } from '../../store/user/store';

import { PX } from '../../contrib/ui';
import { withContext } from '../../contrib/utils';
import Button from '../Button';
import CreditCard from '../CreditCard';
import CreditCardList from '../CreditCardList/CreditCardList';
import { Card, FancyCards } from '../FancyCards/FancyCards';
import { FormConsumer, FormProvider, IFormAttributes } from '../Forms';

const ADDPAYMENTHEIGHT = (220 + 28) * Math.max(PX, 0.8); // this view doesnt scale lineally with PX because of stripe...
const BILLINGBASEHEIGHT = (97 + 28) * PX;
const CREDITCARDSIZE = (59 + 14) * PX;

export enum Views {
  'ADD_PAYMENT',
  'EMPTY',
  'SELECT_PAYMENT'
}

type IconType = 'select' | 'delete';

interface IProps extends RouteComponentProps {
  actions: {
    errors: IErrorsActions;
    user: IProfileActions;
  };
  clubId: number;
  iconType?: IconType;
  next: (clubId: number, payment: IStripeToken | ISource) => void;
  nextButton: string;
  sources?: ISource[];
  selectDisabled?: boolean;
  textButton?: string;
  user: IUserStore;
  selectedCardId?: number;
}

interface IState {
  hasCreditCard: boolean;
  processing: boolean;
  selectedView: Views;
  selectedSource?: ISource;
}

class PaymentManagerComponent extends Component<IProps, IState> {
  public cardElement: any;
  private mounted: boolean;

  constructor(props: IProps) {
    super(props);
    this.mounted = false;

    this.state = {
      hasCreditCard: false,
      processing: false,
      selectedView: Views.EMPTY
    };

    this.cardElement = React.createRef();

    this.next = this.next.bind(this);
    this.onCardComplete = this.onCardComplete.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  public async componentDidMount(): Promise<any> {
    this.mounted = true;
    await this.props.actions.user.getOrRetrieve();
    if (!this.mounted) {
      return;
    }
    const sources = this.props.user.customer ? this.props.user.customer.sources : [];
    if (sources.length || this.props.sources !== undefined) {
      this.setState({
        selectedSource: !!this.props.sources ? this.props.sources[0] : sources[0],
        selectedView: Views.SELECT_PAYMENT
      });
    } else {
      this.setState({
        selectedView: Views.ADD_PAYMENT
      });
    }
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public next(): void {
    this.setState({ processing: true });
    this.props.actions.errors.clear();
    if (this.state.selectedView === Views.ADD_PAYMENT) {
      this.cardElement.current.submit();
    } else {
      this.onPaySelect();
    }
  }

  public onSubmit(token: IStripeToken): any {
    const clubId = this.props.clubId;
    const payment = token || this.state.selectedSource;

    this.props.next(clubId, payment);
    if (!!this.props.sources) {
      this.setState({ processing: false, selectedView: Views.SELECT_PAYMENT });
    }
  }

  public onCardComplete(completed: boolean): void {
    this.setState({ hasCreditCard: completed });
    if (!completed) {
      this.props.actions.errors.clear();
    }
  }

  public selectView = (cardIndex: number) => () => {
    this.setState({ selectedView: cardIndex });
  };

  public onSelectPayment = (selectedSource: ISource) => () => {
    const { sources, selectDisabled } = this.props;
    this.setState({ selectedSource }, () => {
      if (!selectDisabled || sources === undefined || sources === null) {
        this.onPaySelect();
      }
    });
  };

  public onPaySelect(): void {
    const clubId = this.props.clubId;
    const payment = this.state.selectedSource as ISource;

    this.props.next(clubId, payment);
  }

  public render(): JSX.Element {
    const { sources, iconType, textButton, selectedCardId } = this.props;

    return (
      <Consumer>
        {({ errors, user }) => {
          const customerSources = user.customer ? user.customer.sources : [];

          return (
            <FormProvider defaultValues={{}} onSubmit={this.next} validations={{}}>
              <FormConsumer>
                {({ errors: formErrors }: IFormAttributes) => (
                  <div className="payment-manager form">
                    <FancyCards selected={this.state.selectedView}>
                      <Card name="credit-card" height={ADDPAYMENTHEIGHT}>
                        <CreditCard
                          onComplete={this.onCardComplete}
                          onSubmit={this.onSubmit}
                          ref={this.cardElement}
                        />
                      </Card>

                      <Card name="no-credit-card" height={ADDPAYMENTHEIGHT}>
                        <div />
                      </Card>

                      <Card
                        name="credit-card-list"
                        height={BILLINGBASEHEIGHT + customerSources.length * CREDITCARDSIZE}
                      >
                        <CreditCardList
                          iconType={!!iconType ? iconType : 'select'}
                          onAdd={this.selectView(Views.ADD_PAYMENT)}
                          onSelect={this.onSelectPayment}
                          sources={!!sources ? sources : customerSources}
                          textButton={!!textButton ? textButton : ''}
                          selectedCardId={selectedCardId}
                        />
                      </Card>
                    </FancyCards>

                    {errors.membership && <div className="error">{errors.membership.msg}</div>}

                    {this.state.selectedView === Views.ADD_PAYMENT && (
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={
                          formErrors.FORM || !this.state.hasCreditCard || this.state.processing
                        }
                        type="submit"
                        className="button button--shadow button-next"
                      >
                        {this.props.nextButton}
                      </Button>
                    )}
                  </div>
                )}
              </FormConsumer>
            </FormProvider>
          );
        }}
      </Consumer>
    );
  }
}

const PaymentManager = withContext(PaymentManagerComponent);

export { PaymentManager };
